// Updated AccountHealthAnalysis.tsx matching the new pattern
import React, { useMemo, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Target } from "lucide-react";
import { QuickWinCard } from "./QuickWinCard";
import { CopyButton } from "@/components/ui/copy-button";
import { formatNumber } from "@/lib/utils";
import type { OnDisapprovedData } from "@/types/onboarding";

interface AccountHealthAnalysisProps {
  disapprovedData: OnDisapprovedData[] | undefined;
  emptyAdGroupsData: any[] | undefined;
  onAccountHealthCountChange?: (count: number) => void;
  onDisapprovedAdsCountChange?: (count: number) => void;
  onEmptyAdGroupsCountChange?: (count: number) => void;
  focusedSection?: "disapproved-ads" | "empty-ad-groups" | null;
}

export const AccountHealthAnalysis: React.FC<AccountHealthAnalysisProps> = ({
  disapprovedData,
  emptyAdGroupsData,
  onAccountHealthCountChange,
  onDisapprovedAdsCountChange,
  onEmptyAdGroupsCountChange,
  focusedSection,
}) => {
  const disapprovedAdsInsights = useMemo(() => {
    const adData = disapprovedData as OnDisapprovedData[] | undefined;
    if (!adData || adData.length === 0) {
      return { totalDisapproved: 0, affectedCampaigns: 0, worstCampaign: null };
    }

    const campaignGroups: { [key: string]: string[] } = {};
    adData.forEach((ad) => {
      if (!campaignGroups[ad.campaign]) campaignGroups[ad.campaign] = [];
      campaignGroups[ad.campaign].push(ad.adGroupName);
    });

    const worstCampaignName = Object.entries(campaignGroups).reduce(
      (worst, [name, ads]) =>
        ads.length > worst[1].length ? [name, ads] : worst,
      ["", []],
    )[0];

    return {
      totalDisapproved: adData.length,
      affectedCampaigns: Object.keys(campaignGroups).length,
      worstCampaign: worstCampaignName
        ? {
            name: worstCampaignName,
            disapprovedCount: campaignGroups[worstCampaignName].length,
            adGroups: campaignGroups[worstCampaignName],
          }
        : null,
    };
  }, [disapprovedData]);

  const emptyAdGroupsInsights = useMemo(() => {
    const adGroupData = emptyAdGroupsData;
    if (!adGroupData || adGroupData.length === 0) {
      return { totalEmptyGroups: 0, affectedCampaigns: 0, worstCampaign: null };
    }

    const campaignGroups: { [key: string]: string[] } = {};
    adGroupData.forEach((row: any) => {
      if (!campaignGroups[row.campaign]) campaignGroups[row.campaign] = [];
      campaignGroups[row.campaign].push(row.adGroupName);
    });

    const worstCampaignName = Object.entries(campaignGroups).reduce(
      (worst, [name, groups]) =>
        groups.length > worst[1].length ? [name, groups] : worst,
      ["", []],
    )[0];

    return {
      totalEmptyGroups: adGroupData.length,
      affectedCampaigns: Object.keys(campaignGroups).length,
      worstCampaign: worstCampaignName
        ? {
            name: worstCampaignName,
            emptyGroupCount: campaignGroups[worstCampaignName].length,
            adGroups: campaignGroups[worstCampaignName] || [],
          }
        : null,
    };
  }, [emptyAdGroupsData]);

  // Notify parent of total account health issues count
  useEffect(() => {
    if (onAccountHealthCountChange) {
      const totalCount =
        disapprovedAdsInsights.totalDisapproved +
        emptyAdGroupsInsights.totalEmptyGroups;
      onAccountHealthCountChange(totalCount);
    }
  }, [
    disapprovedAdsInsights.totalDisapproved,
    emptyAdGroupsInsights.totalEmptyGroups,
    onAccountHealthCountChange,
  ]);

  useEffect(() => {
    if (onDisapprovedAdsCountChange) {
      onDisapprovedAdsCountChange(disapprovedAdsInsights.totalDisapproved);
    }
  }, [disapprovedAdsInsights.totalDisapproved, onDisapprovedAdsCountChange]);

  useEffect(() => {
    if (onEmptyAdGroupsCountChange) {
      onEmptyAdGroupsCountChange(emptyAdGroupsInsights.totalEmptyGroups);
    }
  }, [emptyAdGroupsInsights.totalEmptyGroups, onEmptyAdGroupsCountChange]);

  // Filter components based on focused section
  const shouldShowComponent = (componentType: string) => {
    if (!focusedSection) return true; // Show all if no focus
    return focusedSection === componentType;
  };

  const gridClass = focusedSection
    ? "space-y-6"
    : "grid grid-cols-1 lg:grid-cols-2 gap-6";

  return (
    <div className={gridClass}>
      {shouldShowComponent("disapproved-ads") && (
        <QuickWinCard
          id="disapproved-ads-analysis"
          title="Disapproved Ads"
          description="These ads are not running due to policy violations"
          icon={<XCircle />}
          severity={
            disapprovedAdsInsights.totalDisapproved > 0 ? "high" : "good"
          }
        >
          {disapprovedAdsInsights.totalDisapproved > 0 &&
          disapprovedAdsInsights.worstCampaign ? (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-red-800">
                      Policy Violations
                    </h4>
                    <p className="text-sm text-red-700">
                      <strong>{disapprovedAdsInsights.totalDisapproved}</strong>{" "}
                      ads disapproved across your account
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Badge variant="secondary">
                      {disapprovedAdsInsights.affectedCampaigns} campaigns
                    </Badge>
                    <CopyButton
                      text={
                        disapprovedData
                          ?.map(
                            (row: any) =>
                              `${row.adGroupName}\t${row.campaign}\t${row.campaignId || "N/A"}`,
                          )
                          .join("\n") || ""
                      }
                      variant="outline"
                      size="sm"
                      className="whitespace-nowrap"
                    >
                      Copy All Data
                    </CopyButton>
                  </div>
                </div>
              </div>

              {/* Complete Disapproved Ads Table */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h5 className="font-medium text-slate-700">
                    All Disapproved Ads (
                    {disapprovedAdsInsights.totalDisapproved} total)
                  </h5>
                  <CopyButton
                    text={
                      disapprovedData
                        ?.map(
                          (row: any) =>
                            `${row.adGroupName}\t${row.campaign}\t${row.campaignId || "N/A"}`,
                        )
                        .join("\n") || ""
                    }
                    variant="outline"
                    size="sm"
                  >
                    Copy All Table Data
                  </CopyButton>
                </div>

                <div className="overflow-x-auto">
                  <div className="min-w-[900px]">
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-slate-100 rounded-t text-xs font-medium text-slate-700">
                      <div className="col-span-3">Ad Group Name</div>
                      <div className="col-span-4">Campaign</div>
                      <div className="col-span-2 text-center">
                        Policy Status
                      </div>
                      <div className="col-span-1 text-center">Priority</div>
                      <div className="col-span-2 text-center">Actions</div>
                    </div>

                    {/* Table Rows - All Disapproved Ads */}
                    <div className="space-y-1">
                      {disapprovedData?.map((row: any, index: number) => (
                        <div
                          key={index}
                          className="grid grid-cols-12 gap-2 px-4 py-3 bg-white border-b border-red-100 hover:bg-red-50 transition-colors text-sm"
                        >
                          <div className="col-span-3 font-medium text-slate-800">
                            <div className="truncate" title={row.adGroupName}>
                              {row.adGroupName}
                            </div>
                          </div>
                          <div className="col-span-4 text-slate-600">
                            <div className="truncate" title={row.campaign}>
                              {row.campaign}
                            </div>
                            <div className="text-xs text-slate-500">
                              Campaign ID: {row.campaignId || "N/A"}
                            </div>
                          </div>
                          <div className="col-span-2 text-center">
                            <Badge
                              variant="outline"
                              className="text-red-700 border-red-300"
                            >
                              Disapproved
                            </Badge>
                          </div>
                          <div className="col-span-1 text-center">
                            <Badge variant="destructive" className="text-xs">
                              High
                            </Badge>
                          </div>
                          <div className="col-span-2 text-center">
                            <CopyButton
                              text={`${row.adGroupName}\t${row.campaign}\t${row.campaignId || "N/A"}`}
                              variant="ghost"
                              size="sm"
                            >
                              Copy
                            </CopyButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Summary Information */}
                <div className="p-3 bg-red-50 border border-red-200 rounded text-sm">
                  <div className="font-medium text-red-800 mb-1">Summary:</div>
                  <div className="text-red-700">
                    • {disapprovedAdsInsights.totalDisapproved} total
                    disapproved ads across{" "}
                    {disapprovedAdsInsights.affectedCampaigns} campaigns
                  </div>
                  <div className="text-red-700">
                    • Worst campaign:{" "}
                    <strong>
                      {disapprovedAdsInsights.worstCampaign?.name}
                    </strong>{" "}
                    with{" "}
                    {disapprovedAdsInsights.worstCampaign?.disapprovedCount}{" "}
                    disapproved ads
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-slate-700">
                  Priority fix: {disapprovedAdsInsights.worstCampaign.name}
                </h5>
                <p className="text-xs text-slate-500 mt-1">
                  {disapprovedAdsInsights.worstCampaign.disapprovedCount}{" "}
                  disapproved ads in this campaign
                  {disapprovedAdsInsights.affectedCampaigns > 1 &&
                    ` • +${disapprovedAdsInsights.affectedCampaigns - 1} more campaigns affected`}
                </p>
              </div>

              <div className="space-y-2">
                <h5 className="font-medium text-slate-700">
                  Recommended Action
                </h5>
                <p className="text-sm text-slate-600">
                  Review and fix policy violations in Google Ads. Most common
                  issues: trademark conflicts, misleading claims, or destination
                  mismatches.
                </p>
              </div>
            </div>
          ) : (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                No disapproved ads found. Excellent compliance!
              </AlertDescription>
            </Alert>
          )}
        </QuickWinCard>
      )}

      {shouldShowComponent("empty-ad-groups") && (
        <QuickWinCard
          id="account-health-analysis"
          title="Empty Ad Groups"
          description="Active ad groups that contain no enabled ads"
          icon={<Target />}
          severity={
            emptyAdGroupsInsights.totalEmptyGroups > 0 ? "medium" : "good"
          }
        >
          {emptyAdGroupsInsights.totalEmptyGroups > 0 &&
          emptyAdGroupsInsights.worstCampaign ? (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-amber-800">
                      Account Clutter
                    </h4>
                    <p className="text-sm text-amber-700">
                      <strong>{emptyAdGroupsInsights.totalEmptyGroups}</strong>{" "}
                      empty ad groups cluttering your account
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Badge variant="secondary">
                      {emptyAdGroupsInsights.affectedCampaigns} campaigns
                    </Badge>
                    <CopyButton
                      text={
                        emptyAdGroupsData
                          ?.map(
                            (row: any) =>
                              `${row.adGroupName}\t${row.campaign}\t${row.campaignId || "N/A"}`,
                          )
                          .join("\n") || ""
                      }
                      variant="outline"
                      size="sm"
                      className="whitespace-nowrap"
                    >
                      Copy All Data
                    </CopyButton>
                  </div>
                </div>
              </div>

              {/* Complete Empty Ad Groups Table */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h5 className="font-medium text-slate-700">
                    All Empty Ad Groups (
                    {emptyAdGroupsInsights.totalEmptyGroups} total)
                  </h5>
                  <CopyButton
                    text={
                      emptyAdGroupsData
                        ?.map(
                          (row: any) =>
                            `${row.adGroupName}\t${row.campaign}\t${row.campaignId || "N/A"}`,
                        )
                        .join("\n") || ""
                    }
                    variant="outline"
                    size="sm"
                  >
                    Copy All Table Data
                  </CopyButton>
                </div>

                <div className="overflow-x-auto">
                  <div className="min-w-[800px]">
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-slate-100 rounded-t text-xs font-medium text-slate-700">
                      <div className="col-span-4">Ad Group Name</div>
                      <div className="col-span-4">Campaign</div>
                      <div className="col-span-2 text-center">Status</div>
                      <div className="col-span-2 text-center">Actions</div>
                    </div>

                    {/* Table Rows - All Empty Ad Groups */}
                    <div className="space-y-1">
                      {emptyAdGroupsData?.map((row: any, index: number) => (
                        <div
                          key={index}
                          className="grid grid-cols-12 gap-2 px-4 py-3 bg-white border-b border-amber-100 hover:bg-amber-50 transition-colors text-sm"
                        >
                          <div className="col-span-4 font-medium text-slate-800">
                            <div className="truncate" title={row.adGroupName}>
                              {row.adGroupName}
                            </div>
                          </div>
                          <div className="col-span-4 text-slate-600">
                            <div className="truncate" title={row.campaign}>
                              {row.campaign}
                            </div>
                            <div className="text-xs text-slate-500">
                              Campaign ID: {row.campaignId || "N/A"}
                            </div>
                          </div>
                          <div className="col-span-2 text-center">
                            <Badge
                              variant="outline"
                              className="text-amber-700 border-amber-300"
                            >
                              No Ads
                            </Badge>
                          </div>
                          <div className="col-span-2 text-center">
                            <CopyButton
                              text={`${row.adGroupName}\t${row.campaign}\t${row.campaignId || "N/A"}`}
                              variant="ghost"
                              size="sm"
                            >
                              Copy
                            </CopyButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Summary Information */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm">
                  <div className="font-medium text-amber-800 mb-1">
                    Summary:
                  </div>
                  <div className="text-amber-700">
                    • {emptyAdGroupsInsights.totalEmptyGroups} total empty ad
                    groups across {emptyAdGroupsInsights.affectedCampaigns}{" "}
                    campaigns
                  </div>
                  <div className="text-amber-700">
                    • Worst campaign:{" "}
                    <strong>{emptyAdGroupsInsights.worstCampaign?.name}</strong>{" "}
                    with {emptyAdGroupsInsights.worstCampaign?.emptyGroupCount}{" "}
                    empty groups
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-slate-700">
                  Priority fix: {emptyAdGroupsInsights.worstCampaign.name}
                </h5>
                <p className="text-xs text-slate-500 mt-1">
                  {emptyAdGroupsInsights.worstCampaign.emptyGroupCount} empty ad
                  groups in this campaign
                  {emptyAdGroupsInsights.affectedCampaigns > 1 &&
                    ` • +${emptyAdGroupsInsights.affectedCampaigns - 1} more campaigns affected`}
                </p>
              </div>

              <div className="space-y-2">
                <h5 className="font-medium text-slate-700">
                  Recommended Action
                </h5>
                <p className="text-sm text-slate-600">
                  Delete empty ad groups or add responsive search ads to them.
                  Empty groups clutter your account and make campaign management
                  unnecessarily complicated.
                </p>
              </div>
            </div>
          ) : (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                All ad groups contain active ads. Your account structure is
                solid!
              </AlertDescription>
            </Alert>
          )}
        </QuickWinCard>
      )}
    </div>
  );
};
